/**
 * UI-8a Task 6 — TenantDetailPage.
 *
 * Per-tenant detail view. Composition:
 *
 *   - Breadcrumb-style back link to the /platform/tenants list.
 *   - PageHeader with tenant.name + a status Tag (severity keyed off status).
 *   - <Card title="Identity"> — slug (code-styled), status, schemaProvisioned
 *     + provisionedAt date when set, createdAt.
 *   - <TenantLimitsCard> — UI-8b Plan selector + 7-field limit overrides.
 *   - Action row: Suspend (with confirmDialog) when active, Reactivate
 *     (instant) when suspended, Provision Now (instant) when
 *     `!schemaProvisioned`.
 *
 * Phase 1 fetches the full list and finds the tenant client-side — there is
 * no GET /tenants/:id endpoint yet. The list is small (platform-level), so
 * this is acceptable. If the tenant id doesn't match anything in the list
 * after the fetch settles we render a "Tenant not found" branch with a back
 * link rather than a perma-spinner.
 *
 * Permission: `platform:tenants_manage`. The page wraps itself in
 * <RequirePermission>; the router does not need additional gating.
 */
import { useState, type JSX } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Tag } from 'primereact/tag'
import { ProgressSpinner } from 'primereact/progressspinner'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'
import { InputText } from 'primereact/inputtext'
import { Dialog } from 'primereact/dialog'

import PageHeader from '../../components/admin/PageHeader.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { TenantLimitsCard } from '../../components/platform/TenantLimitsCard.js'
import { useTenants, type Tenant } from '../../hooks/useTenants.js'
import { useTenantUsers, type TenantUser } from '../../hooks/useTenantUsers.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { formatDate } from '../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function TenantDetailPage(): JSX.Element {
  return (
    <RequirePermission permission="platform:tenants_manage">
      <TenantDetailInner />
    </RequirePermission>
  )
}

function TenantDetailInner(): JSX.Element {
  const toast = useToast()
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const id = params.id

  const { items, loading, refetch, update, provision } = useTenants()
  const tenant = items.find((t) => t.id === id) ?? null
  const { users, loading: usersLoading, refetch: refetchUsers, updatePassword, createUser } = useTenantUsers(id ?? '')

  // --- Legal name editing state -------------------------------------------
  const [legalNameEdit, setLegalNameEdit] = useState<string | null>(null)
  const [savingLegalName, setSavingLegalName] = useState(false)

  // --- Password editing state ---------------------------------------------
  const [passwordEditUser, setPasswordEditUser] = useState<TenantUser | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [savingPassword, setSavingPassword] = useState(false)

  // --- New user creation state --------------------------------------------
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false)
  const [newUserEmail, setNewUserEmail] = useState('')
  const [newUserFullName, setNewUserFullName] = useState('')
  const [newUserPassword, setNewUserPassword] = useState('')
  const [creatingUser, setCreatingUser] = useState(false)

  async function handleSaveLegalName(): Promise<void> {
    if (!tenant || legalNameEdit === null) return
    setSavingLegalName(true)
    try {
      await update(tenant.id, { legalName: legalNameEdit.trim() || null })
      toast.success('Legal name updated')
      setLegalNameEdit(null)
      await refetch()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update legal name')
    } finally {
      setSavingLegalName(false)
    }
  }

  // --- Status-change handlers ----------------------------------------------

  function handleSuspend(t: Tenant): void {
    confirmDialog({
      message:
        'Suspending will block all tenant logins. This is reversible via Reactivate.',
      header: `Suspend ${t.name}`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        try {
          await update(t.id, { status: 'suspended' })
          toast.success(`${t.name} suspended`)
          await refetch()
        } catch (e) {
          toast.error(
            e instanceof Error ? e.message : 'Failed to suspend tenant',
          )
        }
      },
    })
  }

  async function handleReactivate(t: Tenant): Promise<void> {
    try {
      await update(t.id, { status: 'active' })
      toast.success(`${t.name} reactivated`)
      await refetch()
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Failed to reactivate tenant',
      )
    }
  }

  async function handleProvision(t: Tenant): Promise<void> {
    try {
      await provision(t.id)
      toast.success(`${t.name} provisioned`)
      await refetch()
      await refetchUsers()
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Failed to provision tenant',
      )
    }
  }

  async function handleSavePassword(): Promise<void> {
    if (!passwordEditUser || !newPassword) return
    setSavingPassword(true)
    try {
      await updatePassword(passwordEditUser.id, newPassword)
      toast.success('Password updated')
      setPasswordEditUser(null)
      setNewPassword('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update password')
    } finally {
      setSavingPassword(false)
    }
  }

  const isPasswordValid = (pwd: string): boolean => {
    return pwd.length >= 12 && /\d/.test(pwd) && /[A-Z]/.test(pwd)
  }

  async function handleCreateUser(): Promise<void> {
    if (!newUserEmail || !newUserFullName || !newUserPassword) return
    setCreatingUser(true)
    try {
      await createUser(newUserEmail, newUserFullName, newUserPassword)
      toast.success('User created')
      setShowCreateUserDialog(false)
      setNewUserEmail('')
      setNewUserFullName('')
      setNewUserPassword('')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create user')
    } finally {
      setCreatingUser(false)
    }
  }

  // --- Loading / not-found branches ---------------------------------------

  if (loading && tenant === null) {
    return (
      <div className="px-8 py-6 space-y-4">
        <div className="flex justify-center py-12">
          <ProgressSpinner />
        </div>
      </div>
    )
  }

  if (tenant === null) {
    return (
      <div className="px-8 py-6 space-y-4">
        <PageHeader
          title="Tenant not found"
          breadcrumbs={[{ label: 'Tenants', to: '/platform/tenants' }]}
        />
        <div className="rounded border border-gray-200 p-6 text-center">
          <p className="text-gray-700">Tenant not found.</p>
          <Link
            to="/platform/tenants"
            className="mt-2 inline-block text-blue-600 hover:underline"
          >
            Back to Tenants
          </Link>
        </div>
      </div>
    )
  }

  // --- Render --------------------------------------------------------------

  return (
    <div className="px-8 py-6 space-y-4">
      <PageHeader
        title={tenant.name}
        description={`Platform tenant detail — ${tenant.slug}`}
        breadcrumbs={[
          { label: 'Tenants', to: '/platform/tenants' },
          { label: tenant.name },
        ]}
        actions={
          <Tag
            value={tenant.status}
            severity={tenant.status === 'active' ? 'success' : 'warning'}
          />
        }
      />

      <div className="grid gap-4 md:grid-cols-2">
        <Card title="Identity">
          <dl className="space-y-2 text-sm">
            <Row label="Slug">
              <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs">
                {tenant.slug}
              </code>
            </Row>
            <Row label="Status">
              <Tag
                value={tenant.status}
                severity={tenant.status === 'active' ? 'success' : 'warning'}
              />
            </Row>
            <Row label="Provisioned">
              {tenant.schemaProvisioned ? (
                <span className="inline-flex items-center gap-1">
                  <i className="pi pi-check text-green-600" aria-hidden />
                  <span>{formatDate(tenant.provisionedAt)}</span>
                </span>
              ) : (
                <span className="text-gray-400">—</span>
              )}
            </Row>
            <Row label="Created">{formatDate(tenant.createdAt)}</Row>
            <Row label="Legal Name">
              {legalNameEdit !== null ? (
                <span className="flex items-center gap-2">
                  <InputText
                    value={legalNameEdit}
                    onChange={(e) => setLegalNameEdit(e.target.value)}
                    placeholder="Legal entity name"
                    className="flex-1 text-sm"
                    data-testid="legal-name-input"
                    autoFocus
                  />
                  <Button
                    label="Save"
                    size="small"
                    loading={savingLegalName}
                    onClick={() => { void handleSaveLegalName() }}
                    data-testid="legal-name-save"
                  />
                  <Button
                    label="Cancel"
                    size="small"
                    severity="secondary"
                    text
                    onClick={() => setLegalNameEdit(null)}
                    disabled={savingLegalName}
                  />
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className={tenant.legalName ? 'text-gray-900' : 'text-gray-400'}>
                    {tenant.legalName ?? '—'}
                  </span>
                  <Button
                    icon="pi pi-pencil"
                    size="small"
                    text
                    aria-label="Edit legal name"
                    onClick={() => setLegalNameEdit(tenant.legalName ?? '')}
                    data-testid="legal-name-edit-button"
                  />
                </span>
              )}
            </Row>
          </dl>
        </Card>

        <TenantLimitsCard
          tenantId={tenant.id}
          tenant={tenant}
          onPlanChange={() => {
            void refetch()
          }}
        />
      </div>

      {tenant.schemaProvisioned && (
        <Card title="Users">
          {usersLoading ? (
            <div className="flex justify-center py-8">
              <ProgressSpinner />
            </div>
          ) : (
            <div className="space-y-3">
              {users.length === 0 ? (
                <p className="text-gray-500 text-sm">No users found</p>
              ) : (
                users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between rounded border border-gray-200 p-3"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-sm">{user.fullName || user.email}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                      <div className="mt-1 flex gap-1">
                        {user.roles.map((role) => (
                          <Tag key={role} value={role} severity="info" className="text-xs" />
                        ))}
                      </div>
                    </div>
                    <Button
                      label="Edit Password"
                      size="small"
                      text
                      onClick={() => {
                        setPasswordEditUser(user)
                        setNewPassword('')
                      }}
                    />
                  </div>
                ))
              )}
              <Button
                label="Add User"
                icon="pi pi-plus"
                size="small"
                onClick={() => setShowCreateUserDialog(true)}
              />
            </div>
          )}
        </Card>
      )}

      <div className="flex flex-wrap gap-2">
        {tenant.status === 'active' && (
          <Button
            label="Suspend"
            severity="warning"
            onClick={() => {
              handleSuspend(tenant)
            }}
          />
        )}
        {tenant.status === 'suspended' && (
          <Button
            label="Reactivate"
            severity="success"
            onClick={() => {
              void handleReactivate(tenant)
            }}
          />
        )}
        {!tenant.schemaProvisioned && (
          <Button
            label="Provision Now"
            onClick={() => {
              void handleProvision(tenant)
            }}
          />
        )}
        <Button
          label="Back to list"
          severity="secondary"
          text
          onClick={() => {
            navigate('/platform/tenants')
          }}
        />
      </div>

      <ConfirmDialog />

      {/* Password Edit Dialog */}
      <Dialog
        visible={passwordEditUser !== null}
        onHide={() => {
          setPasswordEditUser(null)
          setNewPassword('')
        }}
        header={`Edit Password for ${passwordEditUser?.fullName || passwordEditUser?.email}`}
        modal
        style={{ width: '400px' }}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              New Password
            </label>
            <InputText
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password (min 12 chars, 1 digit, 1 uppercase)"
              className="w-full"
              autoFocus
            />
            <p className="mt-1 text-xs text-gray-500">
              Must be at least 12 characters with at least one digit and one uppercase letter
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              severity="secondary"
              text
              onClick={() => {
                setPasswordEditUser(null)
                setNewPassword('')
              }}
              disabled={savingPassword}
            />
            <Button
              label="Save"
              loading={savingPassword}
              onClick={() => { void handleSavePassword() }}
              disabled={!isPasswordValid(newPassword)}
            />
          </div>
        </div>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog
        visible={showCreateUserDialog}
        onHide={() => {
          setShowCreateUserDialog(false)
          setNewUserEmail('')
          setNewUserFullName('')
          setNewUserPassword('')
        }}
        header="Add New User"
        modal
        style={{ width: '450px' }}
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Email
            </label>
            <InputText
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full"
              autoFocus
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Full Name
            </label>
            <InputText
              value={newUserFullName}
              onChange={(e) => setNewUserFullName(e.target.value)}
              placeholder="John Doe"
              className="w-full"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Temporary Password
            </label>
            <InputText
              type="password"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              placeholder="Enter temporary password (min 12 chars, 1 digit, 1 uppercase)"
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              Must be at least 12 characters with at least one digit and one uppercase letter
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              severity="secondary"
              text
              onClick={() => {
                setShowCreateUserDialog(false)
                setNewUserEmail('')
                setNewUserFullName('')
                setNewUserPassword('')
              }}
              disabled={creatingUser}
            />
            <Button
              label="Create User"
              loading={creatingUser}
              onClick={() => { void handleCreateUser() }}
              disabled={!newUserEmail || !newUserFullName || !isPasswordValid(newUserPassword)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Row({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="flex gap-2">
      <dt className="w-32 shrink-0 text-gray-500">{label}</dt>
      <dd className="flex-1 text-gray-900">{children}</dd>
    </div>
  )
}
